import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
} from "@mui/material"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import { DateRangePicker } from "rsuite"
import Chart from "@/components/charts/Chart"
import { useEffect, useState } from "react"
import { apiFetch } from "@/hooks/apiFetch"
import "rsuite/dist/rsuite.min.css"
import { exportReport } from "@/hooks/useReportExport"
import { useInitBrands } from "@/hooks/useInitBrands"
import { useAppSelector } from "@/lib/hooks"
import { BrandSelect } from "../../Brand/BrandSelect"
import { useReportBranch } from "@/components/Reports/ReportBranchFilter"

const MostSellingProductReport = () => {
  const [salesData, setSalesData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 30)), // Default: Last 30 days
    new Date(),
  ])
  const [limit, setLimit] = useState(10) // Default to top 10 products
  const { branchId } = useReportBranch()

  // export Daily Report
  const handleExport = async () => {
    return exportReport({
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      limit: limit.toString(),
      reportType: "trending-product",
      brand_id: selectedBrandId,
      branchId,
    })
  }

  useInitBrands()
  const selectedBrandId = useAppSelector((state) => state.brand.selectedBrandId)

  useEffect(() => {
    const fetchSalesData = async () => {
      try {
        setLoading(true)
        setError(null)

        const params = new URLSearchParams({
          start_date: dateRange[0].toISOString(),
          end_date: dateRange[1].toISOString(),
          limit: limit.toString(),
          brand_id: selectedBrandId,
        })

        if (branchId) {
          params.set("branch_id", branchId.toString())
        }

        const data = await apiFetch(
          `report/trending-products?${params.toString()}`
        )
        setSalesData(data || [])
      } catch (err) {
        console.error(err)
        setError(
          "Failed to fetch trending product data. Please try again later."
        )
      } finally {
        setLoading(false)
      }
    }

    fetchSalesData()
  }, [dateRange, limit, selectedBrandId, branchId])

  // Prepare chart data
  const chartData =
    salesData.length > 0
      ? {
          labels: salesData.map((item) => item.product_name),
          salesData: salesData.map((item) => parseFloat(item.total_sales)),
          quantityData: salesData.map((item) =>
            parseInt(item.total_quantity_sold)
          ),
        }
      : null

  return (
    <Box sx={{ p: 3 }}>
      <div className="flex justify-between">
        <Typography variant="h6" gutterBottom>
          <div>Most Selling Products Report</div>
        </Typography>
        <div className="w-[300px]">
          <BrandSelect />
        </div>
      </div>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems={"end"}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                Select Date Range
              </Typography>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format="MMM dd, yyyy"
                placeholder="Select date range"
                cleanable={false}
                style={{ width: "100%" }}
                appearance="default"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="body2" gutterBottom>
                Number of Products to Show
              </Typography>
              <select
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value))}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              >
                <option value="5">Top 5</option>
                <option value="10">Top 10</option>
                <option value="20">Top 20</option>
                <option value="50">Top 50</option>
              </select>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                variant="outlined"
                onClick={() => handleExport()}
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "7px",
                  fontWeight: "500",
                  fontSize: "13px",
                  padding: "6px 3px",
                  marginTop: "10px",
                }}
                color="primary"
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <Typography>Loading...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {/* Summary Cards */}
      {!loading && !error && salesData.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Products Sold</Typography>
              <Typography variant="h5" color="primary">
                {salesData
                  .reduce(
                    (sum, item) => sum + parseInt(item.total_quantity_sold),
                    0
                  )
                  .toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Revenue</Typography>
              <Typography variant="h5" color="secondary">
                Rs.{" "}
                {salesData
                  .reduce((sum, item) => sum + parseFloat(item.total_sales), 0)
                  .toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Top Product</Typography>
              <Typography>
                {salesData[0]?.product_name} (
                {salesData[0]?.total_quantity_sold} sold)
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      {chartData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue by Product
                </Typography>
                <Chart
                  type="bar"
                  data={chartData.salesData}
                  labels={chartData.labels}
                  title="Revenue by Product"
                  backgroundColor="rgba(63, 81, 181, 0.5)"
                  borderColor="#3f51b5"
                  tooltipLabel="Revenue"
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Units Sold by Product
                </Typography>
                <Chart
                  type="bar"
                  data={chartData.quantityData}
                  labels={chartData.labels}
                  title="Units Sold by Product"
                  backgroundColor="rgba(76, 175, 80, 0.5)"
                  borderColor="#4caf50"
                  tooltipLabel="Units Sold"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Product Sales Details
          </Typography>
          <DataTable
            columns={columns}
            data={salesData}
            isLoading={loading}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default MostSellingProductReport
